import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { LEVEL_CONFIG, getLevelName, getReadingStage } from '../../../config/levelConfig.js';
import { tapScale } from '../../../lib/motionPresets.js';
import { SpellingWordManager } from '../SpellingWordManager.jsx';

const SESSION_LENGTH_OPTIONS = [10, 15, 20, 25, 30];
const DAILY_LIMIT_OPTIONS = [15, 30, 45, 60, 90, 0];
const DAILY_LIMIT_MAX_INDEX = DAILY_LIMIT_OPTIONS.length - 1;
const DIFFICULTY_OPTIONS = [
  { id: 'easier', label: 'Easier' },
  { id: 'auto', label: "Finley's Pick" },
  { id: 'harder', label: 'Harder' }
];
const EXTRA_TIME_OPTIONS = [15, 30, 60];
const AVATAR_OPTIONS = ['🐬', '🦊', '🦁', '🧠', '🚀', '🌟'];

function sectionHeader(text) {
  return <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-[0.12em] text-finley-text-light">{text}</div>;
}

function cardWrapper(children, className = '') {
  return <div className={`mb-5 rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-4 shadow-finley-sm ${className}`}>{children}</div>;
}

function SegmentedControl({ options, value, onChange }) {
  return (
    <div className="grid gap-2 sm:grid-cols-5">
      {options.map((option) => {
        const isActive = option === value;
        return (
          <motion.button
            key={option}
            {...tapScale}
            onClick={() => onChange(option)}
            className={`min-h-[44px] rounded-finley-md border-2 px-3 py-2 text-sm font-semibold shadow-finley-sm ${
              isActive
                ? 'border-finley-primary bg-finley-primary/10 text-finley-primary'
                : 'border-finley-text-muted/20 bg-finley-bg text-finley-text-light'
            }`}
          >
            {option} min
          </motion.button>
        );
      })}
    </div>
  );
}

function ToggleSwitch({ checked, disabled = false, onClick }) {
  return (
    <motion.button
      type="button"
      {...(!disabled ? tapScale : {})}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-finley-full transition-colors ${
        checked ? 'bg-finley-primary' : 'bg-finley-text-muted/35'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      aria-pressed={checked}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-finley-full bg-white shadow-finley-sm transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </motion.button>
  );
}

function ToggleRow({ title, subtitle, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-finley-text-muted/10 py-4 last:border-b-0 last:pb-0">
      <div className="flex-1 pr-2">
        <div className="text-sm font-semibold text-finley-text">{title}</div>
        <div className="mt-1 text-xs leading-5 text-finley-text-light">{subtitle}</div>
      </div>
      <div className="pt-0.5">
        <ToggleSwitch checked={checked} onClick={onChange} />
      </div>
    </div>
  );
}

function toDailyLimitLabel(value) {
  return value === 0 ? 'No Limit' : `${value} min`;
}

export function SettingsTab({
  profile,
  profiles = [],
  activeId,
  settings,
  authUser,
  onSelectChild,
  onSave,
  onAddChild,
  onRetakePlacement,
  onSignIn,
  onSignOut
}) {
  const [profileEditorId, setProfileEditorId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAge, setEditAge] = useState(7);
  const [editAvatar, setEditAvatar] = useState('🐬');
  const [showPinEditor, setShowPinEditor] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [deleteCandidateId, setDeleteCandidateId] = useState(null);

  const notificationPrefs = settings?.notificationPrefs || {
    sessionReminders: true,
    weeklyProgressReport: true,
    parentTipsUpdates: true,
    milestoneAlerts: true
  };
  const coachingPromptsEnabled = settings?.coachingPromptsEnabled !== false;

  const activeLevel = profile?.tracker?.currentLevel || 1;
  const stage = getReadingStage(activeLevel);
  const currentSessionLength = SESSION_LENGTH_OPTIONS.includes(settings?.tmr) ? settings.tmr : 15;
  const currentDailyLimit = DAILY_LIMIT_OPTIONS.includes(settings?.dailyLimitMin) ? settings.dailyLimitMin : 45;
  const sliderIndex = Math.max(0, DAILY_LIMIT_OPTIONS.indexOf(currentDailyLimit));
  const currentDifficulty = DIFFICULTY_OPTIONS.some((o) => o.id === settings?.difficultyOverride) ? settings.difficultyOverride : 'auto';
  const todayExtra = settings?.todayExtensionMin || 0;
  const finleyChoose = settings?.finleyChoose !== false;
  const parentEmail = authUser?.email || 'Sign in to enable sync and email reports';

  const openProfileEditor = (child) => {
    setProfileEditorId(child.id);
    setEditName(child.name || '');
    setEditAge(child.age || 7);
    setEditAvatar(child.avatar || '🐬');
  };

  const saveProfileEditor = () => {
    if (!profileEditorId || !onSave) return;
    const trimmedName = editName.trim();
    if (!trimmedName) return;
    onSave({
      profileUpdate: {
        childId: profileEditorId,
        name: trimmedName,
        age: Math.max(4, Math.min(12, Number(editAge) || 7)),
        avatar: editAvatar
      }
    });
    setProfileEditorId(null);
  };

  const activeLevelLabel = useMemo(() => LEVEL_CONFIG.map((lv) => ({ value: lv.level, label: `${lv.level}. ${lv.name}` })), []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-5 pb-28">
      {sectionHeader('Child Profiles')}
      {cardWrapper(
        <>
          {profiles.map((child) => {
            const childLevel = child?.tracker?.currentLevel || 1;
            const childStage = getReadingStage(childLevel);
            const isActive = child.id === activeId;
            return (
              <button
                key={child.id}
                onClick={() => (onSelectChild ? onSelectChild(child.id) : null)}
                className={`mb-3 flex min-h-[60px] w-full items-center justify-between rounded-finley-lg border-2 px-3 py-2 text-left last:mb-0 ${
                  isActive
                    ? 'border-finley-primary/40 bg-finley-primary/5'
                    : 'border-finley-text-muted/15 bg-finley-bg'
                }`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-finley-full bg-finley-primary/10 text-xl">
                    {child.avatar || child.name?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-finley-text">{child.name}</div>
                    <div className="truncate text-xs text-finley-text-light">{getLevelName(childLevel)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-finley-full bg-finley-secondary/10 px-2 py-1 text-[11px] font-semibold text-finley-secondary">{childStage?.stageName || 'Reader'}</span>
                  <motion.button
                    type="button"
                    {...tapScale}
                    onClick={(e) => {
                      e.stopPropagation();
                      openProfileEditor(child);
                    }}
                    className="min-h-[36px] rounded-finley-md border-2 border-finley-text-muted/20 bg-finley-bg px-3 text-xs font-semibold text-finley-text-light"
                  >
                    Edit
                  </motion.button>
                </div>
              </button>
            );
          })}
          <motion.button
            {...tapScale}
            onClick={onAddChild}
            className="min-h-[44px] w-full rounded-finley-lg border-2 border-dashed border-finley-text-muted/25 bg-finley-bg px-4 py-2 text-sm font-semibold text-finley-text-light"
          >
            + Add Child
          </motion.button>
        </>
      )}

      {sectionHeader('Session Controls')}
      {cardWrapper(
        <>
          <div className="mb-4 border-b border-finley-text-muted/10 pb-4">
            <div className="mb-2 text-sm font-semibold text-finley-text">Session Length</div>
            <SegmentedControl options={SESSION_LENGTH_OPTIONS} value={currentSessionLength} onChange={(next) => onSave && onSave({ tmr: next })} />
          </div>

          <div className="mb-4 border-b border-finley-text-muted/10 pb-4">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-finley-text">Daily Time Limit</div>
              <div className="rounded-finley-full bg-finley-bg px-2 py-1 text-xs font-semibold text-finley-primary">{toDailyLimitLabel(currentDailyLimit)}</div>
            </div>
            <input
              type="range"
              min={0}
              max={DAILY_LIMIT_MAX_INDEX}
              step={1}
              value={sliderIndex}
              onChange={(e) => {
                const next = DAILY_LIMIT_OPTIONS[Math.max(0, Math.min(DAILY_LIMIT_MAX_INDEX, Number(e.target.value) || 0))];
                if (onSave) onSave({ dailyLimitMin: next });
              }}
              className="h-10 w-full accent-finley-primary"
            />
            <div className="grid grid-cols-6 text-center text-[11px] text-finley-text-light">
              {DAILY_LIMIT_OPTIONS.map((opt) => (
                <span key={opt}>{opt === 0 ? 'No limit' : `${opt}m`}</span>
              ))}
            </div>
          </div>

          <div className="mb-4 border-b border-finley-text-muted/10 pb-4">
            <div className="mb-2 text-sm font-semibold text-finley-text">Difficulty Level</div>
            <div className="grid grid-cols-3 gap-2">
              {DIFFICULTY_OPTIONS.map((option) => {
                const isActive = option.id === currentDifficulty;
                return (
                  <motion.button
                    key={option.id}
                    {...tapScale}
                    onClick={() => onSave && onSave({ difficultyOverride: option.id })}
                    className={`min-h-[44px] rounded-finley-md border-2 px-2 py-2 text-xs font-semibold shadow-finley-sm ${
                      isActive
                        ? 'border-finley-primary bg-finley-primary/10 text-finley-primary'
                        : 'border-finley-text-muted/20 bg-finley-bg text-finley-text-light'
                    }`}
                  >
                    {option.label}
                  </motion.button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-finley-text-light">Finley's Pick lets the adaptive engine choose the best difficulty automatically.</p>
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold text-finley-text">Extend Time Today</div>
            <div className="mb-2 text-xs text-finley-text-light">Current extension: {todayExtra > 0 ? `+${todayExtra} min` : 'None'}</div>
            <div className="grid grid-cols-3 gap-2">
              {EXTRA_TIME_OPTIONS.map((minutes) => (
                <motion.button
                  key={minutes}
                  {...tapScale}
                  onClick={() => onSave && onSave({ todayExtensionMin: minutes })}
                  className={`min-h-[44px] rounded-finley-md border-2 px-3 py-2 text-sm font-semibold shadow-finley-sm ${
                    todayExtra === minutes
                      ? 'border-finley-accent bg-finley-accent/10 text-finley-accent'
                      : 'border-finley-text-muted/20 bg-finley-bg text-finley-text-light'
                  }`}
                >
                  +{minutes}m
                </motion.button>
              ))}
            </div>
          </div>

          <div className="mt-4 border-t border-finley-text-muted/10 pt-4">
            <div className="mb-1 text-sm font-semibold text-finley-text">Let Finley Choose Activities</div>
            <div className="mb-2 text-xs text-finley-text-light">When on, Finley automatically builds the best activity sequence for each session.</div>
            <ToggleSwitch checked={finleyChoose} onClick={() => onSave && onSave({ finleyChoose: !finleyChoose })} />
          </div>

          <div className="mt-4 border-t border-finley-text-muted/10 pt-4">
            <div className="mb-1 text-sm font-semibold text-finley-text">School Words</div>
            <p className="mb-3 text-xs text-finley-text-light">
              Add words from school so Finley can help your child practice them alongside their reading lessons.
            </p>
            <SpellingWordManager
              childId={activeId}
              wordMastery={profile?.tracker?.wordMastery}
              onWordsUpdate={(mastery) => onSave && onSave({ spellingWordMastery: mastery, spellingChildId: activeId })}
            />
          </div>
        </>
      )}

      {sectionHeader('Notifications')}
      {cardWrapper(
        <>
          {/* TODO(C20): Connect these persisted preferences to the notification delivery system when it ships. */}
          <div className="flex items-start justify-between gap-4 border-b border-finley-text-muted/10 py-4 opacity-75">
            <div className="flex-1">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-[15px] font-semibold text-finley-text">Reading session reminders</span>
                <span className="rounded-finley-full bg-finley-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-finley-primary">
                  Coming Soon
                </span>
              </div>
              <p className="m-0 text-[13px] leading-[1.5] text-finley-text-light">
                Push notifications to remind you when it&apos;s time for today&apos;s reading session. We&apos;ll let you know when this is available.
              </p>
            </div>
            <div className="pt-0.5" aria-hidden="true">
              <ToggleSwitch checked={false} disabled />
            </div>
          </div>
          <ToggleRow
            title="Weekly progress email"
            subtitle="A short summary of what your child practiced, their mastery gains, and 2-3 discussion starters you can use this week."
            checked={notificationPrefs.weeklyProgressReport !== false}
            onChange={() => onSave && onSave({ notificationPrefs: { ...notificationPrefs, weeklyProgressReport: !(notificationPrefs.weeklyProgressReport !== false) } })}
          />
          <ToggleRow
            title="Parent coaching tips"
            subtitle="Short, science-backed tips for getting the most out of Finley. Includes the latest research on what actually moves the needle in early literacy."
            checked={notificationPrefs.parentTipsUpdates !== false}
            onChange={() => onSave && onSave({ notificationPrefs: { ...notificationPrefs, parentTipsUpdates: !(notificationPrefs.parentTipsUpdates !== false) } })}
          />
          <ToggleRow
            title="Milestone celebrations"
            subtitle="Instant notification when your child reaches a new reading level or word mastery milestone."
            checked={notificationPrefs.milestoneAlerts !== false}
            onChange={() => onSave && onSave({ notificationPrefs: { ...notificationPrefs, milestoneAlerts: !(notificationPrefs.milestoneAlerts !== false) } })}
          />
          <ToggleRow
            title="Parent coaching prompts"
            subtitle="After each session, Finley suggests a quick 60-second activity to do with your child. Research shows this is one of the highest-impact things a parent can do."
            checked={coachingPromptsEnabled}
            onChange={() => onSave && onSave({ coachingPromptsEnabled: !coachingPromptsEnabled })}
          />
        </>
      )}

      {sectionHeader('Placement')}
      {cardWrapper(
        <>
          <motion.button
            {...tapScale}
            onClick={onRetakePlacement}
            className="mb-3 min-h-[44px] w-full rounded-finley-lg bg-finley-primary px-4 py-3 text-sm font-semibold text-white shadow-finley-sm"
          >
            Retake Placement Quiz
          </motion.button>
          <p className="mb-4 text-xs text-finley-text-light">Start a new placement quiz to reassess your child's reading level.</p>

          <div className="mb-1 text-sm font-semibold text-finley-text">Adjust Level Manually</div>
          <select
            value={activeLevel}
            onChange={(e) => {
              const nextLevel = Number(e.target.value) || activeLevel;
              if (nextLevel !== activeLevel && onSave && activeId) {
                onSave({ levelOverride: { childId: activeId, newLevel: nextLevel } });
              }
            }}
            className="min-h-[44px] w-full rounded-finley-md border-2 border-finley-text-muted/20 bg-finley-bg px-3 text-sm text-finley-text"
          >
            {activeLevelLabel.map((lv) => (
              <option key={lv.value} value={lv.value}>{lv.label}</option>
            ))}
          </select>
          <p className="mt-2 text-xs text-finley-text-light">Override Finley's placement if you think your child should be at a different level.</p>
          <p className="mt-1 text-xs text-finley-text-muted">Finley's adaptive engine works best when it sets the level. Manual adjustment may affect learning accuracy.</p>
        </>
      )}

      {sectionHeader('Account')}
      {cardWrapper(
        <>
          <button onClick={() => window.openPricing && window.openPricing()} className="flex min-h-[44px] w-full items-center justify-between border-b border-finley-text-muted/10 py-3 text-left">
            <span className="text-sm font-semibold text-finley-text">Subscription</span>
            <span className="text-xs text-finley-text-light">Manage</span>
          </button>
          <div className="flex min-h-[44px] w-full items-center justify-between border-b border-finley-text-muted/10 py-3 text-left">
            <span className="text-sm font-semibold text-finley-text">Email</span>
            <span className="max-w-[60%] truncate text-xs text-finley-text-light">{parentEmail}</span>
          </div>
          <button onClick={() => setShowPinEditor((v) => !v)} className="flex min-h-[44px] w-full items-center justify-between border-b border-finley-text-muted/10 py-3 text-left">
            <span className="text-sm font-semibold text-finley-text">Change PIN</span>
            <span className="text-xs text-finley-text-light">Tap to update</span>
          </button>
          {showPinEditor && (
            <div className="mb-2 mt-2 rounded-finley-md bg-finley-bg p-3">
              <div className="mb-2 text-xs text-finley-text-light">Enter a new 4-digit PIN.</div>
              <input
                inputMode="numeric"
                maxLength={4}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                className="mb-2 min-h-[44px] w-full rounded-finley-md border-2 border-finley-text-muted/20 px-3 text-center text-lg tracking-[0.35em]"
                placeholder="••••"
              />
              <motion.button
                {...tapScale}
                onClick={() => {
                  if (newPin.length === 4 && onSave) {
                    onSave({ newPin });
                    setNewPin('');
                    setShowPinEditor(false);
                  }
                }}
                className="min-h-[44px] w-full rounded-finley-md bg-finley-secondary px-3 py-2 text-sm font-semibold text-white"
              >
                Save PIN
              </motion.button>
            </div>
          )}

          {authUser ? (
            <button onClick={onSignOut} className="mt-1 min-h-[44px] w-full rounded-finley-md border-2 border-finley-error/30 bg-finley-error/10 px-3 py-2 text-sm font-semibold text-finley-error">Sign Out</button>
          ) : (
            <button onClick={onSignIn} className="mt-1 min-h-[44px] w-full rounded-finley-md border-2 border-finley-primary/30 bg-finley-primary/10 px-3 py-2 text-sm font-semibold text-finley-primary">Sign In</button>
          )}

        </>
      )}

      {sectionHeader('Privacy & Data')}
      {cardWrapper(
        <>
          <button onClick={() => window.open('https://finleyreads.com/privacy', '_blank')} className="flex min-h-[44px] w-full items-center justify-between border-b border-finley-text-muted/10 py-3 text-left">
            <span className="text-sm font-semibold text-finley-text">Privacy Policy</span>
            <span className="text-xs text-finley-text-light">Open</span>
          </button>
          <button onClick={() => window.open('https://finleyreads.com/terms', '_blank')} className="flex min-h-[44px] w-full items-center justify-between border-b border-finley-text-muted/10 py-3 text-left">
            <span className="text-sm font-semibold text-finley-text">Data Retention Policy</span>
            <span className="text-xs text-finley-text-light">Open</span>
          </button>
          <button
            onClick={() => {
              // TODO(C20): Wire this to a real user data export endpoint.
              window.alert('Data export is coming soon.');
            }}
            className="flex min-h-[44px] w-full items-center justify-between border-b border-finley-text-muted/10 py-3 text-left"
          >
            <span className="text-sm font-semibold text-finley-text">Export My Data</span>
            <span className="text-xs text-finley-text-light">Request</span>
          </button>
          <button
            onClick={() => {
              // TODO(C20): Replace with full delete account flow with backend + legal confirmations.
              window.alert('Delete account flow is not yet available in-app.');
            }}
            className="mt-1 min-h-[44px] w-full rounded-finley-md border-2 border-finley-error/30 bg-finley-error/10 px-3 py-2 text-left text-sm font-semibold text-finley-error"
          >
            Delete Account
          </button>

          <div className="mt-4 rounded-finley-md border-2 border-finley-error/20 bg-finley-error/8 px-3 py-3">
            <div className="mb-1 text-sm font-semibold text-finley-error">Delete Child Data</div>
            <div className="mb-2 text-xs text-finley-text-light">Permanently removes a child's profile and progress. This cannot be undone.</div>
            {profiles.map((child) => (
              <div key={child.id} className="mb-2 flex items-center justify-between last:mb-0">
                <span className="text-sm text-finley-text">{child.name}</span>
                {deleteCandidateId === child.id ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { onSave && onSave({ deleteId: child.id }); setDeleteCandidateId(null); }}
                      className="min-h-[34px] rounded-finley-md border-2 border-finley-error/35 bg-finley-error/10 px-3 text-xs font-semibold text-finley-error"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setDeleteCandidateId(null)}
                      className="min-h-[34px] rounded-finley-md border-2 border-finley-text-muted/20 bg-finley-bg px-3 text-xs font-semibold text-finley-text-light"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteCandidateId(child.id)}
                    disabled={profiles.length <= 1}
                    className="min-h-[34px] rounded-finley-md border-2 border-finley-error/25 bg-finley-bg px-3 text-xs font-semibold text-finley-error disabled:cursor-default disabled:opacity-40"
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
            {profiles.length <= 1 && <div className="mt-1 text-xs text-finley-text-light">Add another child before deleting the last profile.</div>}
          </div>
        </>
      )}

      {profileEditorId && (
        <div className="fixed inset-0 z-30 flex items-end bg-black/40 p-3">
          <div className="w-full rounded-finley-xl bg-finley-bg-card p-4 shadow-finley-lg">
            <div className="mb-2 text-base font-semibold text-finley-text">Edit Child Profile</div>
            <div className="mb-2 text-xs text-finley-text-light">Quick edits for name, age, and avatar.</div>
            <div className="mb-2 grid grid-cols-6 gap-2">
              {AVATAR_OPTIONS.map((avatar) => (
                <button
                  key={avatar}
                  onClick={() => setEditAvatar(avatar)}
                  className={`min-h-[44px] rounded-finley-md border-2 text-xl ${editAvatar === avatar ? 'border-finley-primary bg-finley-primary/10' : 'border-finley-text-muted/20 bg-finley-bg'}`}
                >
                  {avatar}
                </button>
              ))}
            </div>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="mb-2 min-h-[44px] w-full rounded-finley-md border-2 border-finley-text-muted/20 px-3 text-sm"
              placeholder="Child name"
            />
            <input
              type="number"
              min={4}
              max={12}
              value={editAge}
              onChange={(e) => setEditAge(e.target.value)}
              className="mb-3 min-h-[44px] w-full rounded-finley-md border-2 border-finley-text-muted/20 px-3 text-sm"
              placeholder="Age"
            />
            <div className="flex gap-2">
              <button onClick={() => setProfileEditorId(null)} className="min-h-[44px] flex-1 rounded-finley-md border-2 border-finley-text-muted/20 bg-finley-bg text-sm font-semibold text-finley-text-light">Cancel</button>
              <button onClick={saveProfileEditor} className="min-h-[44px] flex-1 rounded-finley-md bg-finley-primary text-sm font-semibold text-white">Save</button>
            </div>
          </div>
        </div>
      )}

      {stage?.stageName ? <p className="sr-only">Current reading stage: {stage.stageName}</p> : null}
    </motion.div>
  );
}
